/* eslint-disable react/jsx-no-bind */
import React from 'react';

import type { ExtensionHandler } from '@atlaskit/editor-common/extensions/extension-handler';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';

import { AgentManagedBodiedExtensionRenderer } from '@atlassian/editor-plugin-agent-managed-extension/AgentManagedBodiedExtensionRenderer';

import type {
	ContentUnifiedQueryMacroRenderedOutputType,
	ContentUnifiedQueryContentNodeType,
} from '@confluence/content-unified-query';
import {
	EXTENSION_TYPE,
	EXTENSION_KEY,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { TeamCalendarMacroLoader } from '@confluence/team-calendar/entry-points/TeamCalendarMacroLoader.ts';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { RENDERER } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { type ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { TabsRenderer } from '@confluence/native-tabs/entry-points/TabsRenderer.ts';
import { EXTENSION_KEY as NATIVE_TABS_EXTENSION_KEY } from '@confluence/native-tabs/entry-points/extensionConstants.ts';
import { UserProfileMacroHandler } from '@confluence/profile-macros/entry-points/UserProfileMacroRenderer.ts';
import { ProfilePictureMacroHandler } from '@confluence/profile-macros/entry-points/ProfilePictureMacroRenderer.ts';
import { PageTreeSSRExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/PageTreeSSRExtensionHandler.ts';
import { PageTreeSearchMacroHandler } from '@confluence/pagetree-search-macro/entry-points/PageTreeSearchMacroRenderer.ts';
import { ContributorsMacroHandler } from '@confluence/contributors-macro/entry-points/ContributorsMacroRenderer.ts';
import { PopularLabelsMacroHandler } from '@confluence/popular-labels-macro/entry-points/PopularLabelsMacroRenderer.ts';
import { ViewPdfMacroHandler } from '@confluence/viewpdf-macro/entry-points/ViewPdfMacroRenderer.ts';
import { WidgetMacroHandler } from '@confluence/widget-macro/entry-points/WidgetMacroRenderer.ts';
import { IFrameMacroHandler } from '@confluence/iframe-macro/entry-points/IFrameMacroRenderer.ts';
import { ContentByLabelSSRExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/ContentByLabelSSRExtensionHandler.ts';
import { PageIncludeSSRExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/PageIncludeSSRExtensionHandler.ts';
import { AttachmentsMacroHandler } from '@confluence/attachments-macro/entry-points/AttachmentsMacroRenderer.ts';
import { BlogPostsSSRExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/BlogPostsSSRExtensionHandler.ts';
import { RecentlyUpdatedMacroHandler } from '@confluence/recently-updated-macro/entry-points/RecentlyUpdatedMacroRenderer.ts';
import { ContentReportTableMacroHandler } from '@confluence/content-report-table-macro/entry-points/ContentReportTableMacroRenderer.ts';
import { TasksReportMacroHandler } from '@confluence/tasks-report-macro/entry-points/TasksReportMacroRenderer.ts';
import { ChangeHistoryMacroHandler } from '@confluence/change-history-macro/entry-points/ChangeHistoryMacroRenderer.ts';
import { ExcerptIncludeSSRExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/ExcerptIncludeSSRExtensionHandler.ts';
import { AnchorExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/AnchorExtensionHandler.ts';
import { ExpandExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/ExpandExtensionHandler.ts';
import { ExcerptExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/ExcerptExtensionHandler.ts';
import { DetailsExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/DetailsExtensionHandler.ts';
import { DetailsSummaryExtensionHandler } from '@confluence/content-renderer-extension-handlers/entry-points/DetailsSummaryExtensionHandler.ts';
import { LoadablePlaceholderServer } from '@confluence/loadable/entry-points/LoadablePlaceholder.ts';
import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { isMigratedMacroImplEnabled } from '@confluence/macro-utils/entry-points/evaluateMacroEnablement.ts';

import { LiveSearchHandler } from './LiveSearchHandler.tsx';
import { TableOfContentsHandler } from './TableOfContentsHandler.tsx';
import { CreateFromTemplateHandler } from './CreateFromTemplateHandler.tsx';
import { NativeEmbedSsrPlaceholder } from './NativeEmbedSsrPlaceholder.tsx';
// eslint-disable-next-line import/no-cycle
import { FabricLegacyMacrosServerRenderer } from './FabricLegacyMacrosServerRenderer.tsx';
import { getSsrMigrationExtHandlerForRenderer } from './getSsrMigrationExtHandlerForRenderer.tsx';
import { PlaceholderAwareInlineNodeRendererWrapper } from './PlaceholderAwareInlineNodeRendererWrapper.tsx';

type Options = {
	adf?: string | null;
	spaceKey?: string | null;
	contentId: string;
	cloudId: string;
	userId: string | null;
	includeSuperbatch?: () => void;
	macroRenderedOutput?:
		| (ContentUnifiedQueryMacroRenderedOutputType | ContentUnifiedQueryContentNodeType | null)[]
		| null;
	mediaToken?: string;
};

export const getSsrExtensionHandlers = ({
	adf,
	contentId,
	spaceKey,
	cloudId,
	userId,
	macroRenderedOutput,
	includeSuperbatch,
	mediaToken,
}: Options): NonNullable<ADFRendererProps['extensionHandlers']> => ({
	[EXTENSION_TYPE.MACRO_CORE]: getMacroExtensionHandlerForRenderer({
		adf,
		contentId,
		spaceKey,
		cloudId,
		userId,
		macroRenderedOutput,
		includeSuperbatch,
		mediaToken,
	}),
	[EXTENSION_TYPE.MACRO_WITH_REFERENCES]: getEmptyHandlers(),
	[EXTENSION_TYPE.MIGRATION]: getSsrMigrationExtHandlerForRenderer({
		contentId,
	}),
	[EXTENSION_TYPE.NATIVE]: expVal('confluence_native_tabs_experiment', 'isEnabled', false)
		? getNativeExtensionHandlerForRenderer()
		: getEmptyHandlers(),
	[EXTENSION_TYPE.TEMPLATE]: getEmptyHandlers(),
});

const getEmptyHandlers = (): ExtensionHandler<any> => createMacroExtensionHandler(() => null);

const getNativeExtensionHandlerForRenderer =
	(): ExtensionHandler<any> => (extension, _doc, actions) => {
		if (extension.extensionKey === NATIVE_TABS_EXTENSION_KEY) {
			return <TabsRenderer node={extension} actions={actions} />;
		}

		return null;
	};

export const getExtensionHandlersMap = ({
	macroDefaultProps,
	extension,
	cloudId,
	contentId,
	spaceKey,
	userId,
	adf,
	mediaToken = undefined,
}: {
	macroDefaultProps: any;
	extension: any;
	cloudId: any;
	contentId: any;
	spaceKey: any;
	userId: any;
	adf: any;
	mediaToken?: undefined;
}): {
	'content-report-table': () => React.JSX.Element | null;
} => {
	function getSsrButtonLabel() {
		const { buttonLabel, createButtonLabel } = macroDefaultProps.node.parameters.macroParams;

		const labelText = buttonLabel?.value || createButtonLabel?.value;
		return labelText || undefined;
	}

	return {
		[EXTENSION_KEY.TOC]: () => (
			<TableOfContentsHandler
				macroDefaultProps={macroDefaultProps}
				adf={adf}
				cloudId={cloudId}
				userId={userId}
			/>
		),
		[EXTENSION_KEY.CREATE_FROM_TEMPLATE]: () => (
			<CreateFromTemplateHandler
				macroDefaultProps={macroDefaultProps}
				cloudId={cloudId}
				userId={userId}
				ssrButtonLabel={getSsrButtonLabel()}
			/>
		),
		[EXTENSION_KEY.LIVE_SEARCH]: () => <LiveSearchHandler macroDefaultProps={macroDefaultProps} />,
		[EXTENSION_KEY.DETAILS]: () => (
			<DetailsExtensionHandler
				{...macroDefaultProps}
				Renderer={ADFRenderer}
				isLegacyRenderer={false}
				getExtensionHandlers={getSsrExtensionHandlers}
			/>
		),
		[EXTENSION_KEY.EXPAND]: () => (
			<ExpandExtensionHandler
				{...macroDefaultProps}
				Renderer={ADFRenderer}
				isLegacyRenderer={false}
				getExtensionHandlers={getSsrExtensionHandlers}
			/>
		),
		[EXTENSION_KEY.EXCERPT]: () => (
			<ExcerptExtensionHandler
				{...macroDefaultProps}
				Renderer={ADFRenderer}
				isLegacyRenderer={false}
				getExtensionHandlers={getSsrExtensionHandlers}
				mediaToken={mediaToken}
			/>
		),
		[EXTENSION_KEY.ANCHOR]: () => <AnchorExtensionHandler {...macroDefaultProps} isSSR />,
		[EXTENSION_KEY.EXCERPT_INCLUDE]: () => {
			if (contentId) {
				return (
					<ExcerptIncludeSSRExtensionHandler
						{...macroDefaultProps}
						Renderer={ADFRenderer}
						getExtensionHandlers={getSsrExtensionHandlers}
					/>
				);
			} else {
				return null;
			}
		},
		[EXTENSION_KEY.PAGE_TREE]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.PAGE_TREE)) {
				return (
					<PageTreeSSRExtensionHandler
						{...macroDefaultProps}
						Renderer={ADFRenderer}
						getExtensionHandlers={getSsrExtensionHandlers}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.PAGE_TREE_SEARCH]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.PAGE_TREE_SEARCH)) {
				return (
					<PageTreeSearchMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.RECENTLY_UPDATED]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.RECENTLY_UPDATED)) {
				return (
					<RecentlyUpdatedMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={{
							...extension?.parameters?.macroParams,
							currentSpaceKey: spaceKey,
						}}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.PROFILE_PICTURE]: () => (
			<ProfilePictureMacroHandler
				macroDefaultProps={macroDefaultProps}
				macroParams={extension?.parameters?.macroParams}
			/>
		),
		[EXTENSION_KEY.PROFILE]: () => (
			<UserProfileMacroHandler
				macroDefaultProps={macroDefaultProps}
				macroParams={extension?.parameters?.macroParams}
			/>
		),
		[EXTENSION_KEY.CONTRIBUTORS]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.CONTRIBUTORS)) {
				return (
					<ContributorsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.POPULAR_LABELS]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.POPULAR_LABELS)) {
				return (
					<PopularLabelsMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={{
							...extension?.parameters?.macroParams,
							currentSpaceKey: spaceKey,
						}}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.VIEWPDF]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.VIEWPDF)) {
				return (
					<ViewPdfMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.WIDGET]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.WIDGET)) {
				return (
					<WidgetMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.IFRAME]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.IFRAME)) {
				return (
					<IFrameMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.CONTENT_BY_LABEL]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.CONTENT_BY_LABEL)) {
				return (
					<ContentByLabelSSRExtensionHandler
						{...macroDefaultProps}
						Renderer={ADFRenderer}
						getExtensionHandlers={getSsrExtensionHandlers}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.INCLUDE]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.INCLUDE)) {
				return (
					<PageIncludeSSRExtensionHandler
						{...macroDefaultProps}
						Renderer={ADFRenderer}
						getExtensionHandlers={getSsrExtensionHandlers}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.ATTACHMENTS]: () =>
			isMigratedMacroImplEnabled(EXTENSION_KEY.ATTACHMENTS) ? (
				<AttachmentsMacroHandler
					macroDefaultProps={macroDefaultProps}
					macroParams={extension?.parameters?.macroParams}
				/>
			) : null,
		[EXTENSION_KEY.BLOG_POSTS]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.BLOG_POSTS)) {
				return (
					<BlogPostsSSRExtensionHandler
						{...macroDefaultProps}
						Renderer={ADFRenderer}
						getExtensionHandlers={getSsrExtensionHandlers}
					/>
				);
			}
			return null;
		},
		'content-report-table': (): React.JSX.Element | null => {
			if (isMigratedMacroImplEnabled('content-report-table')) {
				return (
					<ContentReportTableMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={{
							...extension?.parameters?.macroParams,
							currentSpaceKey: spaceKey,
						}}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.TASKS_REPORT_MACRO]: () => {
			if (isMigratedMacroImplEnabled('tasks-report-macro')) {
				return (
					<TasksReportMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.CHANGE_HISTORY]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.CHANGE_HISTORY)) {
				return (
					<ChangeHistoryMacroHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			return null;
		},
		[EXTENSION_KEY.DETAILS_SUMMARY]: () => {
			if (isMigratedMacroImplEnabled(EXTENSION_KEY.DETAILS_SUMMARY)) {
				return (
					<DetailsSummaryExtensionHandler
						macroDefaultProps={macroDefaultProps}
						macroParams={extension?.parameters?.macroParams}
					/>
				);
			}
			return null;
		},
		...(expValEquals('cc-engagement-team-calendar-macro-afm-migration', 'isEnabled', true)
			? {
					[EXTENSION_KEY.TEAM_CALENDARS]: () => (
						<TeamCalendarMacroLoader
							extensionNode={extension}
							macroDefaultProps={macroDefaultProps}
						/>
					),
				}
			: {}),
		// the hide macro should render nothing in renderer
		...(expVal('platform_editor_hide_extension_renderer_support', 'isEnabled', false)
			? { [EXTENSION_KEY.HIDE]: () => <div data-extension-key="hide" /> }
			: {}),
		/* These macros have React implementation on SPA */
		[EXTENSION_KEY.REDACTED_MACRO_PLACEHOLDER]: () => null,
		[EXTENSION_KEY.CONFLUENCE_DATABASES]: () => null,
		// Native-embed extensions use dynamic keys (e.g. 'native-embed:whiteboard',
		// 'native-embed:db'). When the current extension is a native-embed, add an
		// SSR placeholder entry so the map lookup succeeds and we avoid the legacy
		// macro renderer (which would show "Unknown macro").
		...(extension?.extensionKey?.startsWith(EXTENSION_KEY.NATIVE_EMBED)
			? {
					[extension.extensionKey]: () =>
						fg('native_embeds_ssr_placeholder_killswitch') ? (
							<div data-testid="native-embed-ssr-placeholder" />
						) : (
							<NativeEmbedSsrPlaceholder extension={extension} />
						),
				}
			: {}),
		...(extension?.extensionKey === EXTENSION_KEY.AGENT_MANAGED_BLOCK &&
		expVal('agent-managed_blocks_mvp', 'isEnabled', false)
			? {
					[EXTENSION_KEY.AGENT_MANAGED_BLOCK]: () => (
						<AgentManagedBodiedExtensionRenderer node={extension} readOnly />
					),
				}
			: {}),
	};
};

const getMacroExtensionHandlerForRenderer = ({
	adf,
	contentId,
	spaceKey,
	cloudId,
	userId,
	includeSuperbatch,
	macroRenderedOutput,
	mediaToken,
}): ExtensionHandler<any> =>
	createMacroExtensionHandler((extension) => {
		const { type, extensionKey } = extension;
		const macroId = extension?.parameters?.macroMetadata?.macroId?.value;

		const macroDefaultProps: ExtensionHandlerProps = {
			node: extension,
			contentId,
			spaceKey,
			mode: RENDERER,
			extensionKey,
		};

		// This callback won't be available on a recursive call for handlers from bodied macros
		// but it only needs to be called on the top level of recursion.
		if (includeSuperbatch) {
			includeSuperbatch();
		}

		const serverExtensionHandlersMap = getExtensionHandlersMap({
			adf,
			userId,
			contentId,
			spaceKey,
			cloudId,
			extension,
			macroDefaultProps,
			mediaToken,
		});

		const handlerContent = serverExtensionHandlersMap[extensionKey];

		// When early webpanel initialization is enabled, Connect resources are preloaded during SSR.
		// To prevent flickering during React hydration, we need to hide Connect macros in the SSR phase
		// by setting renderedByProvider=false. After hydration, when macros are re-rendered through
		// the provider flow (LegacyMacroRendererComponent), they will have renderedByProvider=true
		const shouldHideDuringSSR =
			fg('content_early_connect_iframe_initialization') ||
			expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false);

		const content =
			typeof handlerContent === 'function' ? (
				handlerContent()
			) : (
				<FabricLegacyMacrosServerRenderer
					macroRenderedOutput={macroRenderedOutput}
					macroDefaultProps={macroDefaultProps}
					macroOutput={extension?.parameters?.macroOutput}
					renderedByProvider={!shouldHideDuringSSR}
				/>
			);

		// This boundary is not directly used but it provides reference for fixing nested div under span.
		// See next/packages/ssr-scripts-utils/src/fixUnbalancedPlaceholderCommentMarkers.ts for details
		return (
			<RendererActionsContext>
				<LoadablePlaceholderServer placeholderId={`legacy-macro-container-${macroId}`}>
					{
						// Mimic the logic in platform/packages/editor/renderer/src/ui/ExtensionRenderer.tsx
						// We don't update the platform code for 2 reasons:
						// 1. Backwards compatibility. I don't know if other product supplies this wrapper already (unlikely)
						// 2. More importantly this needs to be inside the legacy-macro-container-${macroId} boundary.
						//    Inline macros has <span> nested in <div> problem. We are using the boundary as guide to fix them.
						//    See next/packages/ssr-scripts-utils/src/fixUnbalancedPlaceholderCommentMarkers.ts
						type === 'inlineExtension' ? (
							<PlaceholderAwareInlineNodeRendererWrapper macroId={macroId}>
								{content}
							</PlaceholderAwareInlineNodeRendererWrapper>
						) : (
							content
						)
					}
				</LoadablePlaceholderServer>
			</RendererActionsContext>
		);
	});
